import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { TagRow } from './Marquee';

export const Hero = () => {
  const { t } = useTranslation();
  const chips = t('hero.chips', { returnObjects: true }) || [];
  const bullets = t('hero.operatorBullets', { returnObjects: true }) || [];

  return (
    <section id="top" className="relative border-b border-ink" data-testid="hero">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-14 md:pb-24 relative z-10">
        {/* Volume / issue caption */}
        <div className="font-mono uppercase text-[10px] md:text-xs tracking-widest2 text-ink/70 flex items-center gap-4 mb-6 md:mb-8">
          <span>{t('hero.issueVol')}</span>
          <span aria-hidden>—</span>
          <span>{t('hero.issueName')}</span>
          <span aria-hidden>—</span>
          <span>{t('hero.issueYear')}</span>
        </div>

        <div className="grid grid-cols-12 gap-8 lg:gap-12">
          {/* Left: giant headline */}
          <div className="col-span-12 lg:col-span-8">
            <TagRow items={chips} />

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 md:mt-14 leading-none"
            >
              {/* Line 1: SCRAPPY WORK */}
              <span className="display block text-[16vw] md:text-[13vw] lg:text-[11rem] text-ink leading-[0.9]">
                {t('hero.line1')}
              </span>
              {/* Line 2: serif italic 'Sharp' */}
              <span className="serif-italic block text-[13vw] md:text-[9vw] lg:text-[8rem] text-ink leading-[1] mt-4 md:mt-6">
                {t('hero.line2_italic')}
              </span>
              {/* Line 3: SYSTEMS with coral highlight block */}
              <span className="block leading-none mt-3 md:mt-4">
                <span
                  className="display inline-block text-[16vw] md:text-[13vw] lg:text-[11rem] text-ink leading-[0.9]"
                  style={{
                    backgroundColor: '#FF553B',
                    padding: '0.06em 0.18em 0.02em 0.08em',
                  }}
                >
                  {t('hero.line3_hl')}
                </span>
              </span>
              {/* Line 4: MARKETING with cyan block */}
              <span className="block leading-none mt-4 md:mt-6">
                <span
                  className="display inline-block text-[16vw] md:text-[13vw] lg:text-[11rem] text-ink leading-[0.9]"
                  style={{
                    backgroundColor: '#4CDCE6',
                    padding: '0.06em 0.18em 0.02em 0.08em',
                  }}
                >
                  {t('hero.line4_hl')}
                </span>
              </span>
              {/* Line 5: serif italic 'that moves.' */}
              <span className="serif-italic block text-[13vw] md:text-[9vw] lg:text-[8rem] text-ink leading-[1] mt-4 md:mt-6">
                {t('hero.line5_italic')}
              </span>
            </motion.h1>
          </div>

          {/* Right column: post-it + operator card + availability */}
          <div className="col-span-12 lg:col-span-4 flex flex-col gap-6 mt-4 lg:mt-2">
            {/* Yellow post-it */}
            <motion.div
              initial={{ opacity: 0, y: 20, rotate: -3 }}
              animate={{ opacity: 1, y: 0, rotate: 2.5 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ rotate: -1, y: -4, transition: { duration: 0.4 } }}
              className="self-start w-full max-w-xs px-5 py-5 shadow-[6px_8px_0_#0B0B0B]"
              style={{ background: '#F5C846', border: '1px solid #0B0B0B' }}
              data-testid="hero-postit"
            >
              <p className="serif-italic text-lg md:text-xl text-ink leading-snug">
                {t('hero.postitLine1')}<br />
                {t('hero.postitLine2')}<br />
                <span className="underline decoration-ink underline-offset-2">{t('hero.postitLine3')}</span>
              </p>
            </motion.div>

            {/* Dark operator card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="relative bg-ink text-page p-6 md:p-7"
              style={{ border: '1px solid #0B0B0B' }}
              data-testid="hero-operator-card"
            >
              <p className="font-mono uppercase text-xs tracking-widest2 text-lime mb-3">— {t('hero.aboutOperator')}</p>
              <h3 className="display text-4xl md:text-5xl text-page leading-none">{t('hero.operatorName1')}</h3>
              <h3 className="display text-4xl md:text-5xl text-page leading-none">{t('hero.operatorName2')}</h3>
              <p className="serif-italic text-lg text-page/80 mt-3 mb-5">{t('hero.operatorTitle')}</p>
              <ul className="space-y-1.5">
                {bullets.map((b, i) => (
                  <li key={i} className="font-mono text-xs md:text-sm text-page/90 flex gap-2">
                    <span aria-hidden className="text-lime">▸</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="absolute -bottom-3 right-4 px-3 py-1 font-mono uppercase text-[10px] tracking-widest2 bg-sage text-ink border border-ink">
                {t('hero.availableNow')}
              </div>
            </motion.div>

            {/* Lime availability strip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="bg-lime text-ink px-5 py-4"
              style={{ border: '1px solid #0B0B0B' }}
              data-testid="hero-availability"
            >
              <p className="font-mono uppercase text-[10px] tracking-widest2 mb-1.5">{t('hero.nowTakingLabel')}</p>
              <p className="font-mono uppercase text-sm tracking-tight leading-snug">{t('hero.nowTakingText')}</p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
